import React from "react";
import {
  FaCalendar,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone,
  FaGlobe,
  FaIdCard,
  FaFileAlt,
  FaInfoCircle,
  FaFacebook,
  FaTwitter,
  FaInstagram,
} from "react-icons/fa";
import StarRating from "./components/StarRating";

const ArenaProfile = ({ arena, onClose }) => {
  const renderInfo = (icon, label, value) => (
    <p className="flex items-start sm:items-center mb-2 text-[0.85rem] sm:text-[0.9rem] max-[480px]:text-[0.7rem] flex-wrap sm:flex-nowrap">
      <span className="mr-2 text-[0.9rem] sm:text-[1rem] flex-shrink-0">{icon}</span>
      <span className="font-bold mr-2 flex-shrink-0">{label}:</span>
      <span className="break-words">{value || "N/A"}</span>
    </p>
  );

  function formatDate(isoDate) {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  return (
    <div className="fixed inset-0 bg-[rgba(0,0,0,0.7)] flex justify-center items-center z-[1000] p-2 sm:p-4">
      <div className="relative bg-white p-4 sm:p-6 md:p-8 rounded-[8px] w-[95%] sm:w-[90%] md:w-[85%] lg:w-[80%] max-w-[800px] max-h-[90vh] overflow-y-auto">
        <button
          className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-transparent border-0 text-[0.85rem] sm:text-[1rem] cursor-pointer text-[#333] hover:text-red-500 transition-colors z-10"
          onClick={onClose}
        >
          Close
        </button>
        <h2 className="text-[1.2rem] sm:text-[1.5rem] md:text-[1.8rem] mb-3 sm:mb-4 pr-12 sm:pr-0">
          {arena.name || "Unnamed Arena"}
        </h2>

        {arena.logo && (
          <img
            src={`data:image/png;base64,${arena.logo}`}
            alt={`${arena.Arena_name} logo`}
            className="max-w-[100px] sm:max-w-[120px] md:max-w-[150px] h-auto mb-4"
          />
        )}

        {/* Institute Information */}
        {arena.institute && (
          <div className="bg-[#f8f9fa] p-3 sm:p-4 rounded-[8px] mb-4 sm:mb-6 border border-[#e9ecef]">
            <h3 className="text-[0.95rem] sm:text-[1rem] md:text-[1.2rem] mt-4 sm:mt-6 mb-2">
              Associated Institute
            </h3>
            <div className="flex flex-col sm:flex-row items-start sm:items-center mb-4 gap-3 sm:gap-0">
              {arena.institute.logo && (
                <img
                  src={`data:image/png;base64,${arena.institute.logo}`}
                  alt={`${arena.institute.name} logo`}
                  className="max-w-[60px] sm:max-w-[70px] md:max-w-[80px] h-auto sm:mr-4 rounded"
                />
              )}
              <div>
                <h4 className="text-[0.85rem] sm:text-[0.9rem] md:text-[1rem] font-bold text-[#333]">{arena.institute.name}</h4>
                <p className="text-[#6c757d] text-[0.75rem] sm:text-[0.8rem] md:text-[0.9rem]">{arena.institute.institute_type_name}</p>
                <p className="text-[#6c757d] text-[0.75rem] sm:text-[0.8rem] md:text-[0.9rem]">
                  Established: {formatDate(arena.institute.date_established)}
                </p>
                {arena.institute.website && (
                  <a
                    href={arena.institute.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#007bff] text-[0.75rem] sm:text-[0.8rem] md:text-[0.9rem] hover:underline break-all"
                  >
                    Visit Website
                  </a>
                )}
              </div>
            </div>
            {arena.institute.about_institute && (
              <div className="mb-4">
                <h5 className="text-[0.8rem] sm:text-[0.85rem] md:text-[0.9rem] font-semibold text-[#333] mb-2">About Institute</h5>
                <p className="text-[#6c757d] text-[0.75rem] sm:text-[0.8rem] md:text-[0.9rem]">{arena.institute.about_institute}</p>
              </div>
            )}
            {arena.institute.telephone && arena.institute.telephone.length > 0 && (
              <div className="mb-4">
                <h5 className="text-[0.8rem] sm:text-[0.85rem] md:text-[0.9rem] font-semibold text-[#333] mb-2">Institute Contact</h5>
                <p className="text-[#6c757d] text-[0.75rem] sm:text-[0.8rem] md:text-[0.9rem]">
                  Phone: {Array.isArray(arena.institute.telephone) ? arena.institute.telephone.join(", ") : arena.institute.telephone}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Overall Arena Rating */}
        {arena.overall_rating && (
          <div className="bg-[#f8f9fa] p-3 sm:p-4 rounded-[8px] mb-4 sm:mb-6 border border-[#e9ecef]">
            <h3 className="text-[0.95rem] sm:text-[1rem] md:text-[1.2rem] mt-4 sm:mt-6 mb-2">
              Overall Arena Rating
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <StarRating
                rating={arena.overall_rating.avg_rating}
                showNumber={true}
              />
              <span className="text-[#6c757d] text-[0.75rem] sm:text-[0.8rem] md:text-[0.9rem]">
                ({arena.overall_rating.total_ratings} reviews)
              </span>
            </div>
          </div>
        )}

        <div className="mb-4 sm:mb-6">
          {renderInfo(
            <FaCalendar />, 
            "Established", 
            formatDate(arena.date_established)
          )}

          {renderInfo(
            <FaMapMarkerAlt />,
            "Address",
            arena.address
              ? `${arena.address.street || ""} ${
                  arena.address.plot_no || ""
                }, ${arena.address.area || ""}, ${arena.address.city || ""}, ${
                  arena.address.country || ""
                }`
                  .replace(/,\s*,/g, ",")
                  .replace(/^,\s*|,\s*$/g, "") || "N/A"
              : "N/A"
          )}
          {renderInfo(<FaEnvelope />, "Email", arena.email)}
          {renderInfo(<FaPhone />, "Phone", arena.telephone)}
          {renderInfo(
            <FaGlobe />,
            "Website",
            arena.website ? (
              <a href={arena.website} target="_blank" rel="noopener noreferrer">
                {arena.website}
              </a>
            ) : (
              "N/A"
            )
          )}
          {renderInfo(
            <FaIdCard />,
            "Business Registration No",
            arena.business_registration_number
          )}
          {renderInfo(<FaFileAlt />, "Tax No", arena.tax_payer_number)}
          {renderInfo(
            <FaFileAlt />,
            "Other License",
            arena.licenses && arena.licenses.length > 0
              ? arena.licenses
                  .map(
                    (license) =>
                      `${license.title}${
                        license.reference_number
                          ? ` (Ref: ${license.reference_number})`
                          : ""
                      }`
                  )
                  .join(", ")
              : "N/A"
          )}
          {renderInfo(<FaInfoCircle />, "About", arena.about_arena)}
        </div>

        <h3 className="text-[0.95rem] sm:text-[1rem] md:text-[1.2rem] mt-4 sm:mt-6 mb-2">Sports Facilities</h3>
        <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))] md:grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3 sm:gap-4">
          {arena.facilities &&
            arena.facilities.map((facility, index) => (
              <div key={index} className="bg-[#f0f0f0] p-3 sm:p-4 rounded">
                <h4 className="text-[0.85rem] sm:text-[0.9rem] md:text-[1rem] font-semibold mb-2">{facility.sport}: </h4>
                <p className="text-[0.75rem] sm:text-[0.8rem] md:text-[0.85rem] mb-1">
                  <strong>Details:</strong> {facility.about_facility || "N/A"}
                </p>
                <p className="text-[0.75rem] sm:text-[0.8rem] md:text-[0.85rem] mb-1">
                  <strong>Seating Capacity:</strong> {facility.seating_capacity || "N/A"}
                </p>
                <p className="text-[0.75rem] sm:text-[0.8rem] md:text-[0.85rem] mb-1">
                  <strong>Standing Capacity:</strong> {facility.standing_capacity || "N/A"}
                </p>
                <p className="text-[0.75rem] sm:text-[0.8rem] md:text-[0.85rem] mb-1">
                  <strong>Rate:</strong> {facility.rate ? `$${facility.rate}` : "N/A"}
                </p>

                {/* Facility Rating */}
                <div className="mt-2 flex flex-wrap items-center gap-1 sm:gap-2 text-[0.75rem] sm:text-[0.8rem] md:text-[0.85rem]">
                  <strong>Rating: </strong>
                  <StarRating
                    rating={facility.avg_rating || 0}
                    showNumber={true}
                  />
                  <span className="text-[#6c757d] text-[0.7rem] sm:text-[0.8rem] md:text-[0.9rem]">
                    ({facility.total_ratings || 0} reviews)
                  </span>
                </div>
                {facility.images && facility.images.length > 0 && (
                  <div className="mt-2">
                    <p className="text-[0.75rem] sm:text-[0.8rem] md:text-[0.85rem]">
                      <strong>Facility Images:</strong>
                    </p>
                    <div className="flex flex-wrap gap-2 sm:gap-[10px] mt-2 sm:mt-[10px]">
                      {facility.images.map((image, imgIndex) => (
                        <img
                          key={imgIndex}
                          src={`data:image/png;base64,${image}`}
                          alt={`${facility.sport} facility ${imgIndex + 1}`}
                          className="w-[80px] h-[60px] sm:w-[120px] sm:h-[80px] md:w-[150px] md:h-[100px] object-cover rounded"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
        </div>

        <h3 className="text-[0.95rem] sm:text-[1rem] md:text-[1.2rem] mt-4 sm:mt-6 mb-2">Contact Persons</h3>
        <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))] md:grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3 sm:gap-4">
          {arena.contacts &&
            arena.contacts.map((person, index) => (
              <div key={index} className="bg-[#f0f0f0] p-3 sm:p-4 rounded">
                <p className="text-[0.75rem] sm:text-[0.8rem] md:text-[0.85rem] mb-1">
                  <strong>Name: </strong>
                  {`${person.first_name || ""} ${person.middle_name || ""} ${
                    person.last_name || ""
                  }`}
                </p>
                <p className="text-[0.75rem] sm:text-[0.8rem] md:text-[0.85rem] mb-1 break-all">
                  <strong>Email: </strong> {person.email || "N/A"}
                </p>
                <p className="text-[0.75rem] sm:text-[0.8rem] md:text-[0.85rem] mb-1">
                  <strong>Phone No: </strong> {person.phone || "N/A"}
                </p>
              </div>
            ))}
        </div>

        <h3 className="text-[0.95rem] sm:text-[1rem] md:text-[1.2rem] mt-4 sm:mt-6 mb-2">Arena Photos</h3>
        <div className="grid grid-cols-2 sm:grid-cols-[repeat(auto-fill,minmax(120px,1fr))] md:grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2 sm:gap-3 md:gap-4">
          {arena.pictures &&
            arena.pictures.map((photo, index) => (
              <img
                key={index}
                src={`data:image/png;base64,${photo}`}
                alt={`Arena photo ${index + 1}`}
                className="w-full h-[80px] sm:h-[100px] md:h-[150px] object-cover rounded"
              />
            ))}
        </div>

        <h3 className="text-[0.95rem] sm:text-[1rem] md:text-[1.2rem] mt-4 sm:mt-6 mb-2">Company Profile Photos</h3>
        <div className="grid grid-cols-2 sm:grid-cols-[repeat(auto-fill,minmax(120px,1fr))] md:grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2 sm:gap-3 md:gap-4">
          {arena.company_profile_photos &&
            arena.company_profile_photos.map((photo, index) => (
              <img
                key={index}
                src={`data:image/png;base64,${photo}`}
                alt={`Company profile photo ${index + 1}`}
                className="w-full h-[80px] sm:h-[100px] md:h-[150px] object-cover rounded"
              />
            ))}
        </div>

        <h3 className="text-[0.95rem] sm:text-[1rem] md:text-[1.2rem] mt-4 sm:mt-6 mb-2">Social Media</h3>
        <div className="mt-3 sm:mt-4">
          {renderInfo(
            <FaFacebook />,
            "Facebook",
            arena.facebook ? (
              <a
                href={arena.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#007bff] hover:underline break-all"
              >
                {arena.facebook}
              </a>
            ) : (
              "N/A"
            )
          )}
          {renderInfo(
            <FaTwitter />,
            "Twitter",
            arena.twitter ? (
              <a href={arena.twitter} target="_blank" rel="noopener noreferrer" className="text-[#007bff] hover:underline break-all">
                {arena.twitter}
              </a>
            ) : (
              "N/A"
            )
          )}
          {renderInfo(
            <FaInstagram />,
            "Instagram",
            arena.instagram ? (
              <a
                href={arena.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#007bff] hover:underline break-all"
              >
                {arena.instagram}
              </a>
            ) : (
              "N/A"
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ArenaProfile;
